import { useEffect, useState } from "react";
import moment from "moment";
import Api from "lib/api/httpRequestHelper";
import {
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
} from "@material-ui/core";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import FromToDateField from "components/_refactor/fields/from-to-date";
import HeaderClientes from "components/_refactor/headers/clients";
import StructureFields from "components/_refactor/fields/structure";
import ClearButton from "components/_refactor/button/clear";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import InputText from "components/_refactor/text/input";
import MaskAndValid from "utils/maskAndValid";
import TableRelatorioTitulos from "./components/table-relatorio-titulos";

const data30DaysAgo = moment().subtract(30, "days").format("DD/MM/YYYY");
const today = moment().format("DD/MM/YYYY");

const dataBaseOptions = [
  { id: "DATA_EMISSAO", Nome: "Data Emissão" },
  { id: "DATA_VENCIMENTO", Nome: "Data Vencimento" },
];

const situationOptions = [
  { value: "TODOS", Nome: "Todos", id: 1 },
  { value: "ABERTOS", Nome: "Abertos", id: 2 },
  { value: "PAGOS", Nome: "Pagos", id: 3 },
  { value: "VENCIDOS", Nome: "Vencidos", id: 4 },
  { value: "AVENCER", Nome: "A vencer", id: 5 },
];

const formatDateToApi = (value) =>
  value ? moment(value, "DD/MM/YYYY").format("YYYY-MM-DD") : "";

export default function RelatorioTitulosMassa() {
  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [searching, setSearching] = useState(false);
  const [filters, setFilters] = useState({
    startDate: data30DaysAgo,
    endDate: today,
    dataReferencia: dataBaseOptions[1],
    situation: situationOptions[0],
    nrNF: "",
    nrTitulo: "",
    cnpj: "",
  });
  const [reportList, setReportList] = useState([]);
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(25);
  const [totalRegisters, setTotalRegisters] = useState(0);

  const snackbar = useSnackbar();

  const getReportsList = async ({ page = 0, sizePerPage = 25 }) => {
    setSearching(true);
    try {
      const response = await Api.get(
        "financial-report-detail",
        "financialdocument",
        "?page=" + page + "&pageSize=" + sizePerPage,
        true
      );
      setReportList(response?.Result?.ListTitulos || []);
      setTotalRegisters(response?.Result?.TotalRegistros || 0);
      setPage(response?.Result?.Pagina || 0);
      setSizePerPage(sizePerPage);
    } catch {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar os relatórios. Tente novamente.",
        { severity: "error" }
      );
    } finally {
      setSearching(false);
    }
  };

  const handleGenerateReport = async () => {
    setSearching(true);
    try {
      await Api.post(
        "financial-report-create",
        "financialdocument",
        {
          idLoja: structureFilters?.idLoja,
          ...(structureFilters?.idMarca && {
            idMarca: structureFilters?.idMarca,
          }),
          idGrupoEconomico: structureFilters?.idGrupoEconomico,
          situacao: filters.situation?.value,
          dtIni: formatDateToApi(filters.startDate),
          dtFim: formatDateToApi(filters.endDate),
          porEmissao: filters.dataReferencia?.id === "DATA_EMISSAO",
          nrNF: filters.nrNF,
          nrTitulo: filters.nrTitulo,
          ...(filters.cnpj &&
            MaskAndValid.validateCNPJ(filters.cnpj) && {
              cnpjLoja: filters.cnpj.replace(/\D/g, ""),
            }),
        },
        true
      );
      snackbar.showMessage("Relatório gerado com sucesso.", {
        severity: "success",
      });
      getReportsList({ page: 0, sizePerPage });
    } catch {
      snackbar.showMessage(
        "Ocorreu um erro ao gerar o relatório. Tente novamente.",
        { severity: "error" }
      );
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    getReportsList({ page: 0, sizePerPage });
  }, []);

  const handleClearFilters = () => {
    setClearFilters(true);
    setFilters({
      startDate: data30DaysAgo,
      endDate: today,
      dataReferencia: dataBaseOptions[1],
      situation: situationOptions[0],
      nrNF: "",
      nrTitulo: "",
      cnpj: "",
    });
    setTimeout(() => {
      setClearFilters(false);
    }, 1000);
  };

  const canGenerate =
    Boolean(filters.startDate) && Boolean(filters.endDate);

  return (
    <>
      <HeaderClientes
        section="Relatório de títulos em massa"
        subsection="Financeiro"
        tooltipMessage="Para garantir melhor performance e usabilidade, a extração deste relatório será realizada em segundo plano (background). Isso permite que você continue utilizando a ferramenta do Portal Karsten normalmente enquanto o relatório é processado. Assim que a geração for concluída, o arquivo ficará disponível para download na central de relatório de títulos em massa."
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <StructureFields
                fieldsOrder={["economicGroup", "store"]}
                hideFields={["channel", "representative"]}
                clearFilters={clearFilters}
                onStructureFieldsChange={setStructureFilters}
                disableAll={searching}
                onStructureFieldsLoad={setStructureLoading}
                handleSearch={() =>
                  getReportsList({ page: 0, sizePerPage })
                }
              />
              <FromToDateField
                label="Período"
                initialDate={filters.startDate}
                finalDate={filters.endDate}
                setInitialDate={(value) =>
                  setFilters({ ...filters, startDate: value })
                }
                setFinalDate={(value) =>
                  setFilters({ ...filters, endDate: value })
                }
                disabled={searching || structureLoading.length > 0}
              />
              <Grid item xs={12} md={6} lg={4}>
                <AutoCompleteField
                  label="Data de Referência"
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, dataReferencia: value }))
                  }
                  value={filters.dataReferencia}
                  options={dataBaseOptions}
                  disabled={searching}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <AutoCompleteField
                  label="Situação do título"
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, situation: value }))
                  }
                  value={filters.situation}
                  options={situationOptions}
                  disabled={searching}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <InputText
                  label="CNPJ da loja"
                  value={filters.cnpj}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      cnpj: MaskAndValid.maskCNPJ(e.target.value),
                    }))
                  }
                  disabled={searching}
                  error={!!filters.cnpj && !MaskAndValid.validateCNPJ(filters.cnpj)}
                  errorMessage={
                    filters.cnpj && !MaskAndValid.validateCNPJ(filters.cnpj)
                      ? "CNPJ inválido"
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <InputText
                  label="Nº da Nota"
                  value={filters.nrNF}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, nrNF: e.target.value }))
                  }
                  disable={searching}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <InputText
                  label="Nº do Título"
                  value={filters.nrTitulo}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, nrTitulo: e.target.value }))
                  }
                  disable={searching}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleGenerateReport()}
                  disabled={
                    searching ||
                    !canGenerate ||
                    structureLoading.length > 0 ||
                    (!!filters.cnpj && !MaskAndValid.validateCNPJ(filters.cnpj))
                  }
                >
                  {searching ? "Gerando Relatório..." : "Gerar Relatório"}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ClearButton
                  disable={searching || structureLoading.length > 0}
                  onClick={handleClearFilters}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid item xs={12}>
          <TableRelatorioTitulos
            tableData={reportList}
            page={page}
            totalRegistros={totalRegisters}
            onTableChange={(page, sizePerPage) =>
              getReportsList({ page, sizePerPage })
            }
            sizePerPage={sizePerPage}
            reshearch={getReportsList}
          />
        </Grid>
      </Container>
    </>
  );
}
